// Portfolio Tracker — content strings.
//
// Strings here are portable to Contentful — when the registration sweep
// happens, this object becomes the Contentful entry payloads. See
// `docs/features/2026.07.06-senior-market/2026.05.20-001-contentful-sweep-manifest.md` for the
// per-key mapping. These fallback strings are deliberately price-agnostic:
// the real dollar amounts (which may vary by Senior session tier) live only in
// the Contentful entry, so a missing or not-yet-loaded module never renders
// stale or incorrect prices. See the sweep manifest's Portfolio Tracker
// pricing caveat.
//
// `arrivalDateTemplate` is the one entry that still carries a placeholder
// (`##PortfolioArrivalDate##`) — that value is per-subject runtime data,
// substituted by the consumer at render time via `substitute()`.

import {
  getMediaContentItemValueFromContentModule,
  makeContentModuleGetter
} from '@/composables/contentful'

import type { ContentfulContentModule } from '#contentful/ContentfulContentModule'

// Flat content object — the source of truth for all Portfolio Tracker copy.
// Kept separate (rather than collapsing duplicates like `shipped` and
// `outForDelivery` status sentences) so Marketing can diverge the copy
// without code changes.
export const trackerContent = {
  // Modal chrome
  heading: 'Welcome To Your Cart!',
  closeAriaLabel: 'Close',
  legalCopy:
    'If you do not purchase a package or return the portfolio before your deadline, your credit/debit card will be charged the full price of the portfolio plus shipping & tax.',

  // Status sentences — one per `PortfolioTrackerDisplayStage`
  statusSentencePreparing: 'Your Portfolio is being prepared',
  statusSentenceShipped: 'Your Portfolio is on the way',
  statusSentenceOutForDelivery: 'Your Portfolio is on the way',
  statusSentenceDelivered: 'Your Portfolio has been delivered',
  statusSentenceBilled: 'You purchased your Portfolio',
  statusSentenceReturned: 'Your Portfolio has been returned',

  // Status panel — arrival date + track link
  arrivalDateTemplate: 'Will arrive on ##PortfolioArrivalDate##',
  trackLinkLabel: 'Track Portfolio',

  // Entry-point link rendered in `SubjectDetail` (the PLP header row) that
  // opens the tracker modal. Distinct from `trackLinkLabel` ("Track Portfolio"),
  // which is the link *inside* the modal's status panel.
  subjectLinkLabel: 'Portfolio',

  // Portfolio product photo — stand-in `placehold.co` URL until the real
  // asset lands in Contentful with the registration sweep. Sized 2x the
  // desktop image-column width (369px) for retina; mobile shrinks via CSS.
  productImageUrl: 'https://placehold.co/738x528?text=Portfolio+Product',
  productImageAlt: 'Portfolio product',

  // Progress-gauge stage labels
  gaugeLabelPreparing: 'Preparing',
  gaugeLabelShipped: 'Shipped',
  gaugeLabelOutForDelivery: 'Out for Delivery',
  gaugeLabelDelivered: 'Delivered',

  // Options panel ("3 easy ways to purchase or return"). Stored as flat
  // key/value pairs so each entry maps 1:1 to a `senior-portfolio-tracker-option-N-*`
  // Contentful key during the registration sweep (see the sweep manifest).
  // The consumer-facing array shape is assembled by `trackerOptions` below.
  optionsHeading: '3 easy ways to purchase or return at free of charge:',
  option1Title: 'Purchase with a Package',
  option1Description: 'Before Your Deadline',
  option2Title: 'Purchase as a Standalone Product',
  option2Description: 'Before Your Deadline',
  option3Title: 'Return Free of Charge',
  option3Description:
    'Use the return label provided in your portfolio to return it within 60 days from your photo session.'
}

export interface TrackerOption {
  title: string
  description: string
}

export type TrackerContent = typeof trackerContent

// Resolves all Portfolio Tracker copy from its Contentful content module,
// falling back to the hardcoded `trackerContent` per field. Returns the static
// object verbatim when the module is absent (not yet loaded / fetch failed).
// `productImageUrl` resolves from the lone Media item; every other field is a
// text item. Identifiers map 1:1 to the content-model guide `2026.06.08-003`.
export function resolveTrackerContent(module: ContentfulContentModule | null): TrackerContent {
  if (!module) {
    return trackerContent
  }
  const get = makeContentModuleGetter(module)
  const productImage = getMediaContentItemValueFromContentModule(
    module,
    'senior-portfolio-tracker-product-image'
  )
  return {
    heading: get('senior-portfolio-tracker-heading', trackerContent.heading),
    closeAriaLabel: get('senior-portfolio-tracker-close-aria', trackerContent.closeAriaLabel),
    legalCopy: get('senior-portfolio-tracker-legal-copy', trackerContent.legalCopy),
    statusSentencePreparing: get(
      'senior-portfolio-tracker-status-preparing',
      trackerContent.statusSentencePreparing
    ),
    statusSentenceShipped: get(
      'senior-portfolio-tracker-status-shipped',
      trackerContent.statusSentenceShipped
    ),
    statusSentenceOutForDelivery: get(
      'senior-portfolio-tracker-status-out-for-delivery',
      trackerContent.statusSentenceOutForDelivery
    ),
    statusSentenceDelivered: get(
      'senior-portfolio-tracker-status-delivered',
      trackerContent.statusSentenceDelivered
    ),
    statusSentenceBilled: get(
      'senior-portfolio-tracker-status-billed',
      trackerContent.statusSentenceBilled
    ),
    statusSentenceReturned: get(
      'senior-portfolio-tracker-status-returned',
      trackerContent.statusSentenceReturned
    ),
    arrivalDateTemplate: get(
      'senior-portfolio-tracker-arrival-date-template',
      trackerContent.arrivalDateTemplate
    ),
    trackLinkLabel: get('senior-portfolio-tracker-track-link-label', trackerContent.trackLinkLabel),
    subjectLinkLabel: get('senior-portfolio-tracker-link-label', trackerContent.subjectLinkLabel),
    productImageUrl: productImage?.fields?.file?.url ?? trackerContent.productImageUrl,
    productImageAlt: get(
      'senior-portfolio-tracker-product-image-alt',
      trackerContent.productImageAlt
    ),
    gaugeLabelPreparing: get(
      'senior-portfolio-tracker-gauge-label-preparing',
      trackerContent.gaugeLabelPreparing
    ),
    gaugeLabelShipped: get(
      'senior-portfolio-tracker-gauge-label-shipped',
      trackerContent.gaugeLabelShipped
    ),
    gaugeLabelOutForDelivery: get(
      'senior-portfolio-tracker-gauge-label-out-for-delivery',
      trackerContent.gaugeLabelOutForDelivery
    ),
    gaugeLabelDelivered: get(
      'senior-portfolio-tracker-gauge-label-delivered',
      trackerContent.gaugeLabelDelivered
    ),
    optionsHeading: get('senior-portfolio-tracker-options-heading', trackerContent.optionsHeading),
    option1Title: get('senior-portfolio-tracker-option-1-title', trackerContent.option1Title),
    option1Description: get(
      'senior-portfolio-tracker-option-1-description',
      trackerContent.option1Description
    ),
    option2Title: get('senior-portfolio-tracker-option-2-title', trackerContent.option2Title),
    option2Description: get(
      'senior-portfolio-tracker-option-2-description',
      trackerContent.option2Description
    ),
    option3Title: get('senior-portfolio-tracker-option-3-title', trackerContent.option3Title),
    option3Description: get(
      'senior-portfolio-tracker-option-3-description',
      trackerContent.option3Description
    )
  }
}

// Consumer-facing array view of the flat `optionNTitle` / `optionNDescription`
// fields, so the OptionsPanel can `v-for` instead of taking six positional
// props. Built from resolved content so it reflects Contentful when present.
export function resolveTrackerOptions(content: TrackerContent): TrackerOption[] {
  return [
    { title: content.option1Title, description: content.option1Description },
    { title: content.option2Title, description: content.option2Description },
    { title: content.option3Title, description: content.option3Description }
  ]
}

// Re-exported so consumers can keep importing `substitute` from this module
// (here it resolves `arrivalDateTemplate`'s `##PortfolioArrivalDate##` at
// render time); the implementation is shared across the Senior content modules.
export { substitute } from '@/composables/contentful'
